import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import Avatar from "@/components/Avatar";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("thread") || "");
  const [other, setOther] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    if (!active && data.length > 0) {
      setActive(data[0].thread_id);
      setOther(data[0].other_user);
    }
  };

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const recipientFromUrl = params.get("to");
    const threadFromUrl = params.get("thread");
    if (recipientFromUrl) {
      api.get(`/sellers/${recipientFromUrl}`).then(({ data }) => setOther(data.user)).catch(() => {});
    }
    if (threadFromUrl) setActive(threadFromUrl);
  }, [params]);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active}`).then(({ data }) => {
      setMsgs(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
  }, [active]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !other) return;
    try {
      const { data } = await api.post("/messages", {
        thread_id: active, recipient_id: other.id, body: text, files: [],
      });
      setMsgs((m) => [...m, data]);
      setText("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
      <h1 className="font-display font-black text-2xl text-mv-secondary mb-4 lg:mb-6">Messages</h1>
      <div className="grid lg:grid-cols-12 gap-4 lg:gap-6 h-[calc(100dvh-180px)] lg:h-[70vh]">
        <aside className={`${active ? "hidden lg:block" : "block"} lg:col-span-4 bg-white border border-gray-200 rounded-xl overflow-y-auto`} data-testid="thread-list">
          {threads.length === 0 && !other && (
            <div className="p-6 text-sm text-gray-500">No conversations yet. Message a seller from a service page.</div>
          )}
          {threads.map((t) => (
            <button key={t.thread_id} onClick={() => { setActive(t.thread_id); setOther(t.other_user); }}
              className={`w-full text-left p-4 border-b border-gray-100 flex items-center gap-3 ${active === t.thread_id ? "bg-mv-primary/5" : "hover:bg-gray-50"}`}
              data-testid={`thread-${t.thread_id}`}>
              <Avatar src={t.other_user?.avatar_url} name={t.other_user?.name || ""} size={40} className="shrink-0" testId={`thread-avatar-${t.id}`} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-mv-secondary text-sm truncate">{t.other_user?.name || "User"}</div>
                <div className="text-xs text-gray-500 truncate">{t.last_message?.body}</div>
              </div>
              {t.unread > 0 && <span className="text-xs bg-mv-primary text-white rounded-full px-2 py-0.5 shrink-0">{t.unread}</span>}
            </button>
          ))}
        </aside>

        <section className={`${active ? "flex" : "hidden lg:flex"} lg:col-span-8 bg-white border border-gray-200 rounded-xl flex-col overflow-hidden`}>
          <header className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center gap-3">
            <button className="lg:hidden p-1 -ml-1 text-mv-primary font-bold text-sm"
              onClick={() => { setActive(""); setOther(null); }}
              data-testid="messages-back-btn">← Back</button>
            {other ? (
              <>
                <Avatar src={other.avatar_url} name={other.name || ""} size={36} className="shrink-0" testId="active-thread-avatar" />
                <div className="min-w-0">
                  <div className="font-bold text-mv-secondary truncate">{other.name}</div>
                  <div className="text-xs text-gray-500 capitalize">{other.role}</div>
                </div>
              </>
            ) : <div className="text-sm text-gray-500">Select a conversation</div>}
          </header>

          <div className="flex-1 overflow-auto p-4 sm:p-5 space-y-3 bg-gray-50" data-testid="message-list">
            {msgs.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] sm:max-w-md px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-mv-primary text-white" : "bg-white border border-gray-200"}`}>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-gray-400"}`}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {other && (
            <form onSubmit={send} className="border-t border-gray-100 p-3 flex items-center gap-2">
              <Input placeholder="Write a message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" className="text-base" />
              <Button type="submit" disabled={!text.trim()} className="bg-mv-primary text-white shrink-0" data-testid="send-message-btn">
                <PaperPlaneTilt size={18} weight="bold" />
              </Button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
